import { polygon, multiPolygon, lineString, point } from '@turf/helpers';
import area from '@turf/area';
import buffer from '@turf/buffer';
import polygonToLine from '@turf/polygon-to-line';
import distance from '@turf/distance';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import bearing from '@turf/bearing';
import destination from '@turf/destination';
import kinks from '@turf/kinks';
import _ from 'lodash';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import polygonClipping from 'polygon-clipping';
import { Point, LineString, Feature, MultiPolygon, Polygon } from 'geojson';
import { Coordinate } from 'ol/coordinate';
import { NavigationPoint } from './types';
import { alertWarn } from '../../alertDispatcher';
import { SoilCore } from '../../db';
import { calcLineDistance } from '../../utils';

export type PCPoint = [number, number]; // polygon-clipping point
export type PCRing = PCPoint[]; // polygon-clipping ring
export type PCPolygon = PCRing[]; // polygon-clipping polygon
export type PCMultiPolygon = PCPolygon[]; // polygon-clipping multipolygon

export function areNavPointsFromDifferentPolygons(pointA: NavigationPoint, pointB: NavigationPoint): boolean {
  if (
    pointA.type === 'nav-visit' &&
    pointB.type === 'nav-visit' &&
    pointA.borderClusterId !== undefined &&
    pointA.borderClusterId !== pointB.borderClusterId
  ) {
    return true;
  }

  return false;
}

export function toMartinezRings(geometry: Polygon | MultiPolygon): polygonClipping.Geom {
  if (geometry.type === 'Polygon') {
    // Polygon -> PCRing[]
    return geometry.coordinates.map((r) => r.map(([x, y]) => [x, y] as PCPoint));
  } else if (geometry.type === 'MultiPolygon') {
    // MultiPolygon -> PCPolygon[]
    return geometry.coordinates.map((p) => p.map((r) => r.map(([x, y]) => [x, y] as PCPoint)));
  }

  throw new Error('Unsupported geometry type');
}

/**
 * Union clipping geometries, always returning a normalised MultiPolygon.
 * `polygonClipping.union` is used even for a single geometry because it is what
 * normalises the nesting (see `computeGluedBoundaryRings`). Empty input → `[]`.
 */
function unionGeoms(geoms: polygonClipping.Geom[]): polygonClipping.MultiPolygon {
  const inputs = geoms.filter((g) => g && g.length > 0);

  if (inputs.length === 0) {
    return [];
  }

  return polygonClipping.union(inputs[0], ...inputs.slice(1));
}

/**
 * Drop rings polygon-clipping emitted with fewer than 4 positions (a collapsed
 * sliver). turf's `polygon()` / `multiPolygon()` reject those rings with
 * "Each LinearRing of a Polygon must have 4 or more Positions"; a sub-4-position
 * ring encloses no area, so dropping it is lossless. A polygon whose outer ring
 * is degenerate is dropped whole; a degenerate hole is just removed.
 */
function dropDegenerateRings(geom: polygonClipping.MultiPolygon): polygonClipping.MultiPolygon {
  const result: polygonClipping.MultiPolygon = [];

  for (const rings of geom) {
    if (!rings.length || rings[0].length < 4) {
      continue;
    }

    result.push([rings[0], ...rings.slice(1).filter((ring) => ring.length >= 4)]);
  }

  return result;
}

/**
 * Compute (main − unsafeZones) ∪ bridges in Martinez ring form. Throws on
 * empty input or empty result. Shared core for the two public glue variants.
 *
 * Bridges are unioned in AFTER the unsafe zones are subtracted, so a bridge
 * that crosses an unsafe zone keeps that strip walkable: a pull-in bridge is an
 * explicit "the robot may drive here" statement, and that is by design.
 *
 * Every path out of here runs through `polygonClipping`, because
 * `toMartinezRings` mirrors the INPUT nesting (a Polygon yields a bare ring
 * list) while both callers index the result as a MultiPolygon (`.length` =
 * polygon count, `[0]` = one polygon's rings). Letting a bare Polygon escape
 * makes turf read the ring's 5 points as 5 two-position rings and throw
 * "Each LinearRing of a Polygon must have 4 or more Positions".
 */
function computeGluedBoundaryRings(
  main: Feature<Polygon | MultiPolygon>,
  bridges: Feature<Polygon | MultiPolygon>[],
  unsafeZones: Feature<Polygon | MultiPolygon>[],
): polygonClipping.MultiPolygon {
  let mainGeom: polygonClipping.Geom = toMartinezRings(main.geometry);

  if (unsafeZones.length) {
    // Convert unsafe zones to unioned clipping geometry
    let unsafeUnion: polygonClipping.MultiPolygon;

    try {
      unsafeUnion = unionGeoms(unsafeZones.map((f) => toMartinezRings(f.geometry)));
    } catch (e) {
      console.error('glueBoundaryWithBridgesAndUnsafeZones - error', e);
      throw new Error('Could not glue unsafe zones');
    }

    // Subtract unsafe zones from the main boundary. Bridges are added back
    // afterwards on purpose — see the doc comment above.
    mainGeom = polygonClipping.difference(mainGeom, unsafeUnion);

    if (!mainGeom || mainGeom.length === 0) {
      throw new Error('Unsafe zone subtraction resulted in empty geometry');
    }
  }

  let glued: polygonClipping.MultiPolygon;

  try {
    // Union field and bridges (this also normalises the nesting when there are
    // no bridges at all, since `union` always returns a MultiPolygon).
    glued = unionGeoms([mainGeom, ...bridges.map((f) => toMartinezRings(f.geometry))]);
  } catch (e) {
    console.error('glueBoundaryWithBridgesAndUnsafeZones - error', e);
    throw new Error(`Could not glue the main boundary with bridges`);
  }

  const result = dropDegenerateRings(glued);

  if (result.length === 0) {
    throw new Error('Union failed: no resulting geometry');
  }

  return result;
}

export function glueBoundaryWithBridgesAndUnsafeZones(
  main: Feature<Polygon | MultiPolygon>,
  bridges: Feature<Polygon | MultiPolygon>[],
  unsafeZones: Feature<Polygon | MultiPolygon>[] = [],
): Feature<Polygon> {
  const subtracted = computeGluedBoundaryRings(main, bridges, unsafeZones);

  // Convert the result into one polygon with holes
  const polys = subtracted.map((rings) => polygon(rings));
  polys.sort((a, b) => area(b) - area(a));

  // At the end we should receive a single Polygon, not a Multipolygon
  // if all bridges are in place. If a bridge is missing to some
  // polygon, then we don't care about it - skipping it.
  // Thus, we can return the biggest Polygon here: polys[0] after sorting.
  return polys[0];
}

/**
 * Same as `glueBoundaryWithBridgesAndUnsafeZones` but preserves ALL disjoint
 * polygons in the result instead of returning only the largest. Use this for
 * visualization where we want to show every safe area, including pieces that
 * aren't connected to a path's start point. Returns a Polygon when the result
 * is a single piece, or a MultiPolygon otherwise.
 */
export function glueBoundaryWithBridgesAndUnsafeZonesKeepAll(
  main: Feature<Polygon | MultiPolygon>,
  bridges: Feature<Polygon | MultiPolygon>[],
  unsafeZones: Feature<Polygon | MultiPolygon>[] = [],
): Feature<Polygon | MultiPolygon> {
  const subtracted = computeGluedBoundaryRings(main, bridges, unsafeZones);

  if (subtracted.length === 1) {
    return polygon(subtracted[0]);
  }

  return multiPolygon(subtracted);
}

// Not tested yet
// export function modifyBoundaryWithMartinez(
//   main: Feature<Polygon | MultiPolygon>,
//   poly: Feature<Polygon>
// ): Feature<Polygon | MultiPolygon> {
//   const mainGeom = main.geometry;
//   const polyGeom = poly.geometry;

//   // Check spatial relationships
//   const contains = turf.booleanContains(main, poly);
//   const overlaps = turf.booleanOverlap(main, poly);
//   // @ts-ignore
//   const intersects = turf.booleanIntersects(main, poly);

//   const mainRings = toMartinezRings(mainGeom);
//   const polyRings = toMartinezRings(polyGeom);

//   let result: polygonClipping.Geom;

//   if (contains) {
//     // ✅ Case 3: poly is fully inside main → make a hole
//     result = polygonClipping.difference(mainRings, polyRings);
//   } else if (intersects || overlaps) {
//     // ✅ Case 2: poly intersects main → union (glue together)
//     result = polygonClipping.union(mainRings, polyRings);
//   } else {
//     // ✅ Case 1: poly outside main → add as new outer polygon (union works here too)
//     result = polygonClipping.union(mainRings, polyRings);
//   }

//   if (!result || result.length === 0) {
//     throw new Error('Resulting geometry is empty');
//   }

//   // Convert result back to GeoJSON MultiPolygon or Polygon
//   if (result.length === 1) {
//     return turf.multiPolygon(result[0]) as Feature<Polygon | MultiPolygon>;
//   }

//   return turf.multiPolygon(result) as Feature<Polygon | MultiPolygon>;
// }

export function setSampleAndCoreOrder(reorderedCores: SoilCore[]) {
  let activeSampleId: string | undefined = undefined;

  let order = 0;

  reorderedCores.forEach((c) => {
    c.core_number = 0;
    c.getSample()!.order = -1;
  });

  for (const core of reorderedCores) {
    const sample = core.getSample()!;

    if (core.sample_id !== activeSampleId && sample.order === -1) {
      activeSampleId = core.sample_id;
      order += 1;
      sample.order = order;
    }

    core.core_number = sample.getNextAvailableCoreNumber();
  }
}

export function areCoresClose(maxDistanceBetweenCloseCores: number, core1?: SoilCore, core2?: SoilCore): boolean {
  if (!core1 || !core2) {
    return false;
  }

  return areCoresCoordinatesClose(maxDistanceBetweenCloseCores, core1.getCoordinates(), core2.getCoordinates());
}

export function areCoresCoordinatesClose(
  maxDistanceBetweenCloseCores: number,
  core1Coordinate: Coordinate,
  core2Coordinate: Coordinate,
): boolean {
  return (
    calcLineDistance(
      { lat: core1Coordinate[0], lon: core1Coordinate[1], getCoordinates: () => core1Coordinate },
      { lat: core2Coordinate[0], lon: core2Coordinate[1], getCoordinates: () => core2Coordinate },
    ) <= maxDistanceBetweenCloseCores
  );
}

export function generateCoreIdentifierFromCoordinates(sampleId: string, latLon: Coordinate): string {
  return `${sampleId}_${latLon[0]}_${latLon[1]}`;
}

export function parseCoreIdentifier(prevCoreIdentifier: string): { sampleId: string; lat: number; lon: number } {
  const parts = prevCoreIdentifier.split('_');

  if (parts.length !== 3) {
    throw new Error(`Invalid core identifier format: ${prevCoreIdentifier}`);
  }

  return {
    sampleId: parts[0],
    lat: parseFloat(parts[1]),
    lon: parseFloat(parts[2]),
  };
}

export function createBridgePolygonToBoundary(
  start: [number, number],
  boundary: Feature<Polygon | MultiPolygon>,
  minEdgeDistanceMeters = 15,
): Feature<Polygon> {
  const startPt = point(start);

  // Step 1: Pick nearest polygon from MultiPolygon
  let polygonToUse: Feature<Polygon>;

  if (boundary.geometry.type === 'MultiPolygon') {
    const polygons = boundary.geometry.coordinates.map((coords) => polygon(coords));
    polygonToUse = polygons.reduce((closest, current) => {
      const lineAFeatures = extractLineStrings(closest);
      const lineBFeatures = extractLineStrings(current);

      const d1 = distance(startPt, nearestPointOnAnyLine(lineAFeatures, startPt));
      const d2 = distance(startPt, nearestPointOnAnyLine(lineBFeatures, startPt));

      return d2 < d1 ? current : closest;
    });
  } else {
    polygonToUse = boundary as Feature<Polygon>;
  }

  // Step 2: Get nearest point and direction
  const boundaryLine = polygonToLine(polygonToUse);
  const boundaryLineFeatures: Feature<LineString>[] =
    boundaryLine.type === 'FeatureCollection'
      ? (boundaryLine.features as Feature<LineString>[])
      : [boundaryLine as Feature<LineString>];

  const nearest = nearestPointOnAnyLine(boundaryLineFeatures, startPt);
  const direction = bearing(startPt, nearest);
  const distanceToBoundary = distance(startPt, nearest); // in km

  // Step 3: Bridge dimensions
  const forwardBackward = distanceToBoundary + minEdgeDistanceMeters / 1000; // extend equally in both directions
  const sideOffset = minEdgeDistanceMeters / 1000; // full-width on each side

  // Step 4: Compute rectangle corners (clockwise)
  const backCenter = destination(startPt, sideOffset, direction + 180);
  const frontCenter = destination(startPt, forwardBackward, direction);

  const backLeft = destination(backCenter, sideOffset, direction - 90);
  const backRight = destination(backCenter, sideOffset, direction + 90);
  const frontRight = destination(frontCenter, sideOffset, direction + 90);
  const frontLeft = destination(frontCenter, sideOffset, direction - 90);

  const coords: [number, number][] = [
    backLeft.geometry.coordinates as [number, number],
    frontLeft.geometry.coordinates as [number, number],
    frontRight.geometry.coordinates as [number, number],
    backRight.geometry.coordinates as [number, number],
    backLeft.geometry.coordinates as [number, number], // Close the ring
  ];

  return polygon([coords]);
}

function nearestPointOnAnyLine(lines: Feature<LineString>[], pt: Feature<Point>): Feature<Point> {
  return lines.reduce(
    (closest, line) => {
      const candidate = nearestPointOnLine(line, pt);
      const d1 = distance(pt, closest);
      const d2 = distance(pt, candidate);

      return d2 < d1 ? candidate : closest;
    },
    nearestPointOnLine(lines[0], pt),
  );
}

function extractLineStrings(polygon: Feature<Polygon | MultiPolygon>): Feature<LineString>[] {
  const lines = polygonToLine(polygon);

  return lines.type === 'FeatureCollection'
    ? lines.features.filter((f): f is Feature<LineString> => f.geometry.type === 'LineString')
    : lines.geometry.type === 'LineString'
      ? [lines as Feature<LineString>]
      : [];
}

export function isPointSafeInsideMultiPolygon(
  multiPolygon: Feature<MultiPolygon>,
  pointLat: number,
  pointLon: number,
  safeDistanceMeters: number,
): boolean {
  for (const coords of multiPolygon.geometry.coordinates) {
    let polygonFeature: Feature<Polygon> | undefined;

    try {
      polygonFeature = polygon(coords);
    } catch (e) {
      console.error(`isPointSafeInsideMultiPolygon - could not make a polygon feature`, e);
    }

    if (!polygonFeature) {
      continue;
    }

    if (isPointSafeInsidePolygon(polygonFeature, pointLat, pointLon, safeDistanceMeters)) {
      return true;
    }
  }

  return false;
}

/**
 * Whether every outer polygon of a MultiPolygon keeps `pointLat`/`pointLon` at
 * least `safeDistanceMeters` outside its edges. Unused (empty) input is safe.
 */
export function isPointSafeOutsideMultiPolygon(
  multiPolygon: Feature<MultiPolygon>,
  pointLat: number,
  pointLon: number,
  safeDistanceMeters: number,
): boolean {
  for (const coords of multiPolygon.geometry.coordinates) {
    let polygonFeature: Feature<Polygon> | undefined;

    try {
      polygonFeature = polygon(coords);
    } catch (e) {
      console.error(`isPointSafeOutsideMultiPolygon - could not make a polygon feature`, e);
    }

    if (!polygonFeature) {
      continue;
    }

    if (!isPointSafeOutsidePolygon(polygonFeature, pointLat, pointLon, safeDistanceMeters)) {
      return false;
    }
  }

  return true;
}

/**
 * The mirror of `isPointSafeInsidePolygon`: the point must be outside the
 * polygon and at least `safeDistanceMeters` from every one of its edges. This is
 * how UNSAFE zones are judged — a core inside one (or hugging its edge) would be
 * driven through ground the mapmaker marked off-limits.
 */
export function isPointSafeOutsidePolygon(
  polygon: Feature<Polygon>,
  pointLat: number,
  pointLon: number,
  safeDistanceMeters: number,
): boolean {
  const pt = point([pointLon, pointLat]);
  let isInside = false;

  try {
    isInside = booleanPointInPolygon(pt, polygon);
  } catch (error) {
    console.error('booleanPointInPolygon error:', error);

    return false;
  }

  if (isInside) {
    return false;
  }

  const distanceToEdgesMeters = distanceToPolygonEdgesMeters(polygon, pt);

  return distanceToEdgesMeters !== undefined && distanceToEdgesMeters > safeDistanceMeters;
}

export function isPointSafeInsidePolygon(
  polygon: Feature<Polygon>,
  pointLat: number,
  pointLon: number,
  safeDistanceMeters: number,
): boolean {
  const pt = point([pointLon, pointLat]);
  let isInside = false;

  try {
    isInside = booleanPointInPolygon(pt, polygon);
  } catch (error) {
    console.error('booleanPointInPolygon error:', error);

    return false;
  }

  if (!isInside) {
    return false;
  }

  const distanceToEdgesMeters = distanceToPolygonEdgesMeters(polygon, pt);

  return distanceToEdgesMeters !== undefined && distanceToEdgesMeters > safeDistanceMeters;
}

/** Metres from `pt` to the nearest edge (outer ring or hole); undefined when the polygon has no edges. */
function distanceToPolygonEdgesMeters(polygon: Feature<Polygon>, pt: Feature<Point>): number | undefined {
  const line = polygonToLine(polygon);
  let lineFeatures: Feature<LineString>[] = [];

  if (line.type === 'FeatureCollection') {
    lineFeatures = line.features.flatMap((f) => {
      if (f.geometry.type === 'LineString') {
        return [f as Feature<LineString>];
      } else if (f.geometry.type === 'MultiLineString') {
        // Split MultiLineString into individual LineString features
        return f.geometry.coordinates.map((coords) => lineString(coords));
      }

      return [];
    });
  } else if (line.type === 'Feature') {
    if (line.geometry.type === 'LineString') {
      lineFeatures = [line as Feature<LineString>];
    } else if (line.geometry.type === 'MultiLineString') {
      lineFeatures = line.geometry.coordinates.map((coords) => lineString(coords));
    }
  }

  if (lineFeatures.length === 0) {
    return undefined;
  }

  const nearest = nearestPointOnAnyLine(lineFeatures, pt);
  const distanceKm = distance(pt, nearest, { units: 'kilometers' });

  return distanceKm * 1000;
}

/**
 * Finds the maximum buffer distance that can be applied without creating self-intersections
 */
function findMaxSafeBufferDistance(boundary: Feature<Polygon>, targetDistance: number): number {
  // First, try the full target distance - most cases should work fine
  try {
    const targetBuffered = buffer(boundary, -targetDistance, { units: 'meters' });

    if (targetBuffered && targetBuffered.geometry.type === 'Polygon') {
      const selfIntersections = kinks(targetBuffered);
      const polygonArea = area(targetBuffered);

      if (selfIntersections.features.length === 0 && polygonArea > 0) {
        // Success! The target distance works perfectly
        return targetDistance;
      }
    }
  } catch (error) {
    // Target distance failed, fall through to binary search
  }

  // If target distance doesn't work, use binary search to find the maximum safe distance
  let minDistance = 0;
  let maxDistance = targetDistance;
  let safeDistance = 0;

  const tolerance = 0.1; // 10cm tolerance
  const maxIterations = 20;
  let iterations = 0;

  while (maxDistance - minDistance > tolerance && iterations < maxIterations) {
    const testDistance = (minDistance + maxDistance) / 2;
    iterations++;

    try {
      const simpleBuffered = buffer(boundary, -testDistance, { units: 'meters' });

      if (!simpleBuffered || simpleBuffered.geometry.type !== 'Polygon') {
        maxDistance = testDistance;

        continue;
      }

      const selfIntersections = kinks(simpleBuffered);
      const polygonArea = area(simpleBuffered);

      const isValid = selfIntersections.features.length === 0 && polygonArea > 0;

      if (!isValid) {
        maxDistance = testDistance;

        continue;
      }

      safeDistance = testDistance;
      minDistance = testDistance;
    } catch (error) {
      maxDistance = testDistance;
    }
  }

  return Math.max(0, safeDistance);
}

/**
 * Buffers a boundary with validation, finding the maximum safe buffer distance
 * that doesn't create self-intersections
 */
export function bufferBoundary(
  boundary: Feature<Polygon>,
  bufferDistanceMeters: number,
): { polygon: Feature<Polygon>; actualBufferDistance: number } {
  try {
    // Step 1: Find the maximum safe buffer distance using simple buffering
    const maxSafeDistance = findMaxSafeBufferDistance(boundary, bufferDistanceMeters);

    if (maxSafeDistance <= 0) {
      console.warn('Could not find any safe buffer distance, returning original boundary');

      return { polygon: boundary, actualBufferDistance: 0 };
    }

    // Step 2: Apply the safe buffer using simple buffer
    const buffered = buffer(boundary, -maxSafeDistance, { units: 'meters' });

    if (buffered && buffered.geometry.type === 'Polygon') {
      // Perfect - we got a single polygon
      return { polygon: buffered as Feature<Polygon>, actualBufferDistance: maxSafeDistance };
    } else {
      // This shouldn't happen given our validation, but handle it gracefully
      console.warn('Simple buffer unexpectedly created MultiPolygon, taking largest component');
      const multiPolygon = buffered as unknown as Feature<MultiPolygon>;
      const components = multiPolygon.geometry.coordinates.map((coords) => polygon(coords));
      components.sort((a, b) => area(b) - area(a));

      return { polygon: components[0], actualBufferDistance: maxSafeDistance };
    }
  } catch (error) {
    console.warn('bufferBoundaryWithValidationConnected failed:', error);
    // Fall back to the original boundary slightly shrunk
    const fallbackBuffer = Math.min(1, bufferDistanceMeters * 0.1);

    return {
      polygon: buffer(boundary, -fallbackBuffer, { units: 'meters' }) as Feature<Polygon>,
      actualBufferDistance: fallbackBuffer,
    };
  }
}

/**
 * Buffer a polygon inward at the full requested distance, keeping every
 * resulting component.
 *
 * A field ring can legitimately pinch (two lobes joined by a sub-metre neck —
 * e.g. 98.kml poly_id 1 at the pond outlet). The ring is OGC-valid, but any
 * inward offset splits it, so `bufferBoundary`'s single-Polygon validation
 * rejects every distance and `bufferBoundaryPolygonsInward` drops the field.
 *
 * Returns null when the full offset is empty or invalid, so the caller can
 * fall back to the conservative single-distance path. Unlike `bufferBoundary`
 * this never picks one component — callers that can only route in one
 * connected region must not opt in.
 */
function bufferPolygonInwardKeepingParts(
  boundary: Feature<Polygon>,
  distanceMeters: number,
): Feature<Polygon | MultiPolygon> | null {
  try {
    const buffered = buffer(boundary, -distanceMeters, { units: 'meters' });

    if (!buffered || (buffered.geometry.type !== 'Polygon' && buffered.geometry.type !== 'MultiPolygon')) {
      return null;
    }

    if (kinks(buffered).features.length > 0) {
      return null;
    }

    const parts =
      buffered.geometry.type === 'Polygon' ? [buffered.geometry.coordinates] : buffered.geometry.coordinates;

    if (parts.length === 0 || parts.some((rings) => area(polygon(rings)) <= 0)) {
      return null;
    }

    return buffered as Feature<Polygon | MultiPolygon>;
  } catch (error) {
    return null;
  }
}

/**
 * Buffers boundary polygons inward, expands unsafe zones outward,
 * then glues with bridges. This ensures narrow bridges don't limit
 * the buffer distance for main field areas.
 *
 * Order of operations:
 * 1. Buffer each polygon in the boundary inward by bufferDistanceMeters
 * 2. Expand each unsafe zone outward by bufferDistanceMeters
 * 3. Union buffered boundary with bridges (bridges stay at original width)
 * 4. Subtract expanded unsafe zones
 */
export function prepareBufferedBoundary(
  boundary: Feature<Polygon | MultiPolygon>,
  bridges: Feature<Polygon | MultiPolygon>[],
  unsafeZones: Feature<Polygon | MultiPolygon>[],
  bufferDistanceMeters: number,
): Feature<Polygon> {
  const warnings: string[] = [];

  // Step 1: Buffer each polygon in the boundary inward
  const bufferedBoundary = bufferBoundaryPolygonsInward(boundary, bufferDistanceMeters, warnings);

  // Step 2: Expand unsafe zones outward
  const expandedUnsafeZones = unsafeZones
    .map((uz) => expandPolygonOutward(uz, bufferDistanceMeters))
    .filter((uz): uz is Feature<Polygon | MultiPolygon> => uz !== null);

  // Step 3 & 4: Glue buffered boundary + bridges, subtract expanded unsafe zones
  const result = glueBoundaryWithBridgesAndUnsafeZones(bufferedBoundary, bridges, expandedUnsafeZones);

  if (warnings.length > 0) {
    alertWarn(`Boundary buffering issues: ${warnings.join('; ')}`);
  }

  return result;
}

/**
 * Buffers every polygon of a boundary inward by `distanceMeters`.
 *
 * `allowSplit` keeps every component of an inward buffer. A pinched field ring
 * (OGC-valid, but its lobes meet at a sub-metre neck) legitimately splits when
 * inset; without the flag `bufferBoundary`'s single-Polygon validation finds no
 * safe distance and the polygon is counted as collapsed and dropped. Off by
 * default because SmartPathBuilder can only route within one connected region;
 * the safe-routing boundary (display + robot-path verdict) opts in.
 */
export function bufferBoundaryPolygonsInward(
  boundary: Feature<Polygon | MultiPolygon>,
  distanceMeters: number,
  warnings: string[],
  { allowSplit = false }: { allowSplit?: boolean } = {},
): Feature<Polygon | MultiPolygon> {
  const collectParts = (buffered: Feature<Polygon | MultiPolygon>): Polygon['coordinates'][] =>
    buffered.geometry.type === 'Polygon' ? [buffered.geometry.coordinates] : buffered.geometry.coordinates;

  if (boundary.geometry.type === 'Polygon') {
    if (allowSplit) {
      const split = bufferPolygonInwardKeepingParts(boundary as Feature<Polygon>, distanceMeters);

      if (split) {
        return split;
      }
    }

    const { polygon: buffered, actualBufferDistance } = bufferBoundary(boundary as Feature<Polygon>, distanceMeters);

    if (actualBufferDistance < distanceMeters) {
      warnings.push(`buffer reduced from ${distanceMeters}m to ${actualBufferDistance.toFixed(1)}m`);
    }

    return buffered;
  }

  // MultiPolygon: buffer each polygon independently
  const bufferedCoords: Polygon['coordinates'][] = [];
  let collapsedCount = 0;

  for (let i = 0; i < boundary.geometry.coordinates.length; i++) {
    const poly: Feature<Polygon> = polygon(boundary.geometry.coordinates[i]);

    if (allowSplit) {
      const split = bufferPolygonInwardKeepingParts(poly, distanceMeters);

      if (split) {
        bufferedCoords.push(...collectParts(split));

        continue;
      }
    }

    const { polygon: buffered, actualBufferDistance } = bufferBoundary(poly, distanceMeters);

    if (actualBufferDistance <= 0) {
      collapsedCount++;

      continue;
    }

    if (actualBufferDistance < distanceMeters) {
      warnings.push(`polygon ${i + 1}: buffer reduced from ${distanceMeters}m to ${actualBufferDistance.toFixed(1)}m`);
    }

    bufferedCoords.push(buffered.geometry.coordinates);
  }

  if (collapsedCount > 0) {
    warnings.push(`${collapsedCount} polygon(s) collapsed entirely during ${distanceMeters}m buffering`);
  }

  if (bufferedCoords.length === 0) {
    throw new Error('All polygons collapsed after buffering');
  }

  if (bufferedCoords.length === 1) {
    return polygon(bufferedCoords[0]);
  }

  return multiPolygon(bufferedCoords);
}

export function expandPolygonOutward(
  zone: Feature<Polygon | MultiPolygon>,
  distanceMeters: number,
): Feature<Polygon | MultiPolygon> | null {
  const expanded = buffer(zone, distanceMeters, { units: 'meters' });

  if (!expanded) {
    return null;
  }

  return expanded as Feature<Polygon | MultiPolygon>;
}
